import { AxiosError, AxiosRequestConfig } from 'axios';
import { useQuery, UseQueryOptions, UseQueryResult } from 'react-query';

import { API } from './api';

export type QueryKey<URL extends API['url'], Method extends API['method'] = 'GET'> =
  | [
      URL,
      Extract<API, { url: URL; method?: Method }> extends {
        data?: infer D;
        params?: infer P;
      }
        ? Omit<AxiosRequestConfig<D>, 'params' | 'method'> & { method?: Method; params?: P }
        : AxiosRequestConfig
    ]
  | [URL]
  | URL;

export type QueryFnData<URL extends API['url'], Method extends API['method'] = 'GET'> = Extract<
  API,
  { url: URL; method?: Method }
> extends { response: infer R }
  ? R
  : unknown;

export type UseAPIQueryOptions<
  URL extends API['url'],
  Method extends API['method'] = 'GET',
  TData = QueryFnData<URL, Method>
> = UseQueryOptions<QueryFnData<URL, Method>, AxiosError, TData, QueryKey<URL, Method>>;

function useAPIQuery<
  URL extends API['url'] = string,
  Method extends API['method'] = 'GET',
  TData = QueryFnData<URL, Method>
>(
  queryKey: QueryKey<URL, Method>,
  options?: UseAPIQueryOptions<URL, Method, TData>
): UseQueryResult<TData, AxiosError> {
  return useQuery(queryKey, options);
}

export default useAPIQuery;
